import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    Input,
    NgModule,
    OnInit,
} from '@angular/core';
import { Cms } from '@impact/data';
import { first } from 'rxjs/operators';
import { chevronDownIcon } from '../../../icons';
import { SettingsService } from '../../core/settings.service';
import { ExpansionPanelModule } from '../../expansion-panel/expansion-panel.module';
import { JsonLdModule } from '../../json-ld/json-ld.module';
import { MetaService } from '../../meta/meta.service';
import { RichTextModule } from '../../rich-text/rich-text.module';
import { SectionHeaderModule } from '../../section-header/section-header.module';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-accordion-spot',
    template: `
        <div class="accordion-spot" *ngIf="data.items?.length">
            <app-section-header
                *ngIf="data.headerTextPrimary"
                [isCentered]="true"
                [headerTextPrimary]="data.headerTextPrimary"
                [introText]="data.headerTextSecondary"
            >
            </app-section-header>
            <app-expansion-panel class="accordion-spot__item" *ngFor="let item of data.items" [headerClickable]="true">
                <h2 class="accordion-spot__title" expansion-title>{{ item.accordionHeadline }}</h2>
                <ng-container expansion-icon>${chevronDownIcon}</ng-container>
                <app-rich-text class="accordion-spot__content" [html]="item.accordionText"></app-rich-text>
            </app-expansion-panel>
            <ngx-json-ld *ngIf="schema" [json]="schema"></ngx-json-ld>
        </div>
    `,
})
class AccordionSpotComponent implements OnInit {
    @Input() data: Cms.AccordionListSpot;
    schema?: Record<string, any>;

    constructor(
        private readonly metaService: MetaService,
        private readonly settingsService: SettingsService,
        private readonly cd: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        if (this.data.createFAQSchema) {
            this._setJsonLd();
        }
    }

    private async _setJsonLd() {
        if (!this.data.items?.length) return;

        const items = this.data.items;
        const { companyName } = await firstValueFrom(this.settingsService.get());
        const currentUrl = this.metaService.getCurrentUrl();

        this.schema = {
            '@context': 'https://schema.org',
        };

        if (items.length === 1) {
            const item = items[0];
            this.schema['@type'] = 'QAPage';
            this.schema.mainEntity = {
                '@type': 'Question',
                name: item.accordionHeadline,
                answerCount: 1,
                dateCreated: 'this.data.dateUpdated',
                author: {
                    '@type': 'Organization',
                    name: companyName,
                },
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: item.accordionText,
                    dateCreated: 'this.data.dateUpdated',
                    upvoteCount: 1,
                    url: currentUrl,
                    author: {
                        '@type': 'Organization',
                        name: companyName,
                    },
                },
            };
        } else {
            const mainEntity = [];

            for (const item of items) {
                mainEntity.push({
                    '@type': 'Question',
                    name: item.accordionHeadline,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: item.accordionText,
                    },
                });
            }

            this.schema['@type'] = 'FAQPage';
            this.schema.mainEntity = mainEntity;
        }

        this.cd.markForCheck();
    }
}

@NgModule({
    declarations: [AccordionSpotComponent],
    imports: [
        CommonModule,
        ExpansionPanelModule,
        RichTextModule,
        JsonLdModule,
        SectionHeaderModule,
    ],
})
class AccordionSpotModule {}

export const LazySpotComponent = AccordionSpotComponent;
